'use client';

/**
 * Customer forgot password for model 2 (`password_plus_factor`, #652).
 *
 * `request` asks for the username. An email username is sent a reset link
 * (which reopens the login page with `reset_token`, the `link` step); a
 * mobile username is sent a 6-digit code entered here with the new password
 * (`code` step). A reset never signs the customer in.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, KeyRound, Loader2 } from 'lucide-react';
import TurnstileWidget from '@/components/auth/TurnstileWidget';
import CustomerPasswordInput from '@/components/auth/CustomerPasswordInput';
import { validateCustomerPassword } from '@/lib/auth/customerPasswordPolicy';
import type { CustomerPasswordPolicy } from '@/types/organization';
type Step = 'request' | 'email-sent' | 'code' | 'link' | 'done';
export interface CustomerForgotPasswordProps {
  organizationId: string;
  policy: CustomerPasswordPolicy;
  initialUsername?: string;
  /** The emailed link token; opens straight at the new-password step. */
  resetToken?: string | null;
  brand?: {
    name?: string | null;
    logoUrl?: string | null;
    primaryColor?: string | null;
  };
  identityLabel?: string;
  buttonClassName?: string;
  buttonStyle?: React.CSSProperties;
  onBack: () => void;
}
const HEADINGS: Record<Step, string> = {
  request: 'Reset your password',
  'email-sent': 'Check your email',
  code: 'Enter your code',
  link: 'Choose a new password',
  done: 'Password reset'
};
export default function CustomerForgotPassword(props: CustomerForgotPasswordProps) {
  const {
    organizationId,
    policy,
    resetToken,
    brand,
    buttonClassName,
    buttonStyle,
    onBack
  } = props;
  const [step, setStep] = useState<Step>(resetToken ? 'link' : 'request');
  const [username, setUsername] = useState(props.initialUsername ?? '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const resetCaptcha = () => {
    setCaptchaToken(null);
    setTurnstileResetKey(k => k + 1);
  };
  const post = async (path: string, body: Record<string, unknown>) => {
    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...body,
        organizationId,
        captchaToken
      })
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result?.error || 'Something went wrong. Please try again.');
    return result as {
      channel?: string;
    };
  };
  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result_0 = await post('/api/auth/customer-password/reset/request', {
        username,
        orgBrandName: brand?.name,
        orgLogoUrl: brand?.logoUrl,
        orgPrimaryColor: brand?.primaryColor
      });
      setStep(result_0.channel === 'sms' ? 'code' : 'email-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      resetCaptcha();
      setBusy(false);
    }
  };
  const handleComplete = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    setError('');
    if (validateCustomerPassword(password, policy).length > 0) {
      setError('Your password does not meet the requirements');
      return;
    }
    if (password !== confirmPassword) {
      setError('The passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await post('/api/auth/customer-password/reset/complete', step === 'link' ? {
        resetToken,
        password
      } : {
        username,
        code,
        password
      });
      setPassword('');
      setConfirmPassword('');
      setStep('done');
    } catch (err_0) {
      setError(err_0 instanceof Error ? err_0.message : 'Something went wrong. Please try again.');
    } finally {
      resetCaptcha();
      setBusy(false);
    }
  };
  const errorBox = error && <div role="alert" className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
      {error}
    </div>;
  const turnstile = <TurnstileWidget onSuccess={token => setCaptchaToken(token)} onError={() => setCaptchaToken(null)} onExpire={() => setCaptchaToken(null)} resetKey={turnstileResetKey} className="flex justify-center" />;
  const backButton = <Button type="button" variant="outline" className="w-full h-11 glass" onClick={onBack} disabled={busy}>
      Back to sign in
    </Button>;
  return <div className="space-y-4" data-testid="customer-forgot-password">
      <div className="text-center space-y-1">
        <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
          {step === 'done' || step === 'email-sent' ? <CheckCircle className="h-6 w-6 text-green-600" aria-hidden="true" /> : <KeyRound className="h-6 w-6" aria-hidden="true" />}
        </div>
        <h2 className="text-2xl font-bold">{HEADINGS[step]}</h2>
      </div>

      {step === 'request' && <form onSubmit={handleRequest} className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">
            Enter your username. We&apos;ll email you a reset link, or text you a code if your username is a mobile number.
          </p>
          <div className="space-y-2">
            <Label htmlFor="forgot-username">{props.identityLabel ?? 'Email or mobile number'}</Label>
            <Input id="forgot-username" data-testid="forgot-username" type="text" autoComplete="username" value={username} onChange={e_1 => setUsername(e_1.target.value)} required disabled={busy} className="h-11" />
          </div>
          {errorBox}
          {turnstile}
          <Button type="submit" className={buttonClassName} style={buttonStyle} disabled={busy || !captchaToken}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Sending...</> : 'Send reset instructions'}
          </Button>
          {backButton}
        </form>}

      {step === 'email-sent' && <div className="space-y-4 text-center">
          <p role="status" className="text-muted-foreground">
            If an account matches <strong>{username}</strong>, we&apos;ve sent a link to reset your password.
            The link can only be used once.
          </p>
          {backButton}
        </div>}

      {(step === 'code' || step === 'link') && <form onSubmit={handleComplete} className="space-y-4">
          {step === 'code' && <>
              <p role="status" className="text-sm text-muted-foreground text-center">
                If an account matches <strong>{username}</strong>, we&apos;ve sent a 6-digit code by text message.
              </p>
              <div className="space-y-2">
                <Label htmlFor="forgot-code">Code</Label>
                <Input id="forgot-code" data-testid="forgot-code" type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" maxLength={6} value={code} onChange={e_2 => setCode(e_2.target.value.replace(/[^0-9]/g, '').slice(0, 6))} required disabled={busy} className="h-11 text-center tracking-[0.5em] text-lg" />
              </div>
            </>}
          <CustomerPasswordInput mode="create" label="New password" value={password} onChange={setPassword} policy={policy} disabled={busy} />
          <CustomerPasswordInput mode="create" label="Confirm new password" value={confirmPassword} onChange={setConfirmPassword} disabled={busy} />
          {errorBox}
          {turnstile}
          <Button type="submit" className={buttonClassName} style={buttonStyle} disabled={busy || !captchaToken}>
            {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> Saving...</> : 'Reset password'}
          </Button>
          {step === 'code' && <Button type="button" variant="ghost" className="w-full" disabled={busy} onClick={() => {
        setCode('');
        setError('');
        setStep('request');
      }}>
              Send a new code
            </Button>}
          {backButton}
        </form>}

      {step === 'done' && <div className="space-y-4 text-center">
          <p role="status" className="text-muted-foreground">
            Your password has been reset and you have been signed out everywhere.
            Sign in with your new password to continue.
          </p>
          <Button type="button" className={buttonClassName} style={buttonStyle} onClick={onBack}>
            Back to sign in
          </Button>
        </div>}
    </div>;
}